import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";

const AuthContext = createContext();
const ONBOARDING_TIP_SESSION_PREFIX = 'odontoassist:onboarding-tip-shown';

const clearOnboardingTipSession = () => {
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith(ONBOARDING_TIP_SESSION_PREFIX)) sessionStorage.removeItem(key);
  });
};

const accessHomePath = (currentUser) => {
  if (currentUser?.role === 'admin') return '/admin';
  const permissions = new Set(currentUser?.permissions || []);
  if (permissions.has('dashboard.view')) return '/dashboard';
  if (permissions.has('appointments.view')) return '/agenda';
  if (permissions.has('patients.view')) return '/prontuarios';
  if (permissions.has('billing.view')) return '/financeiro';
  return '/ajuda';
};

const clearClinicLocalState = () => {
  ['clinicSettings', 'currentPatientId', 'selectedPatientForOdontogram', 'activeAppointment']
    .forEach((key) => localStorage.removeItem(key));
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuPreferences, setMenuPreferences] = useState({
    hidden_items: [], preset: null, manually_customized: false,
  });
  const [dashboardPreferences, setDashboardPreferences] = useState({
    preset: null, mode: null, sections: {}, blocks: {}, manually_customized: false,
  });
  const [experiencePreferences, setExperiencePreferences] = useState({
    profile: null, mode: null, source: null, version: 1, changed_at: null,
  });
  const [menuPreferencesLoading, setMenuPreferencesLoading] = useState(true);
  const [clinicContexts, setClinicContexts] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(null);
  const [canCreateOwnClinic, setCanCreateOwnClinic] = useState(false);
  const [clinicContextsLoading, setClinicContextsLoading] = useState(true);

  const reloadClinicContexts = async () => {
    try {
      setClinicContextsLoading(true);
      const response = await api.get('/clinic-contexts');
      setClinicContexts(response.data?.items || []);
      setSelectedClinicId(response.data?.selected_clinic_id || null);
      setCanCreateOwnClinic(response.data?.can_create_own_clinic === true);
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar clínicas disponíveis:', error);
      setClinicContexts([]);
      setSelectedClinicId(null);
      setCanCreateOwnClinic(false);
      return null;
    } finally {
      setClinicContextsLoading(false);
    }
  };

  const reloadMenuPreferences = async () => {
    try {
      setMenuPreferencesLoading(true);
      const response = await api.get("/user-preferences");
      setMenuPreferences(response.data?.menu || {
        hidden_items: [], preset: null, manually_customized: false,
      });
      setDashboardPreferences(response.data?.dashboard || {
        preset: null, mode: null, sections: {}, blocks: {}, manually_customized: false,
      });
      setExperiencePreferences(response.data?.experience || {
        profile: null, mode: null, source: null, version: 1, changed_at: null,
      });
    } catch (error) {
      console.error("Erro ao carregar preferências do menu:", error);
      setMenuPreferences({ hidden_items: [], preset: null, manually_customized: false });
      setDashboardPreferences({
        preset: null, mode: null, sections: {}, blocks: {}, manually_customized: false,
      });
      setExperiencePreferences({
        profile: null, mode: null, source: null, version: 1, changed_at: null,
      });
    } finally {
      setMenuPreferencesLoading(false);
    }
  };

  const updateMenuPreferences = async (preferences) => {
    const payload = Array.isArray(preferences)
      ? { hidden_items: preferences, preset: 'custom', manually_customized: true }
      : preferences;
    const response = await api.put("/user-preferences/menu", {
      hidden_items: payload?.hidden_items || [],
      preset: payload?.preset || 'custom',
      manually_customized: payload?.manually_customized ?? true,
    });
    const nextPreferences = response.data?.menu || payload;
    setMenuPreferences(nextPreferences);
    return nextPreferences;
  };

  const updateDashboardPreferences = async (preferences) => {
    const response = await api.put("/user-preferences/dashboard", preferences);
    const nextPreferences = response.data?.dashboard || preferences;
    setDashboardPreferences(nextPreferences);
    return nextPreferences;
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        verifyToken();
      } catch (error) {
        console.error("Error parsing stored user:", error);
        logout(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await api.get(`/auth/me?t=${Date.now()}`);
      const userData = response.data;

      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      await Promise.all([reloadMenuPreferences(), reloadClinicContexts()]);
    } catch (error) {
      console.error("Error verifying token:", error);
      logout(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      clearOnboardingTipSession();
      const response = await api.post("/auth/login", {
        email,
        password,
      });

      const data = response.data;

      if (response.status === 200) {
        if (data.requires_two_factor) {
          return {
            success: true,
            requiresTwoFactor: true,
            challengeToken: data.challenge_token,
          };
        }
        setToken(data.access_token);
        localStorage.setItem("token", data.access_token);
        let authenticatedUser = data.user;
        try {
          const meResponse = await api.get(`/auth/me?t=${Date.now()}`);
          authenticatedUser = meResponse.data;
        } catch (meError) {
          console.warn("Não foi possível atualizar o contexto de acesso:", meError);
        }
        setUser(authenticatedUser);
        localStorage.setItem("user", JSON.stringify(authenticatedUser));
        await Promise.all([reloadMenuPreferences(), reloadClinicContexts()]);

        if (
          authenticatedUser?.subscription_status === "inactive" &&
          authenticatedUser?.role !== "admin"
        ) {
          setTimeout(() => {
            window.location.href = "/assinatura";
          }, 100);
        }

        return { success: true, user: authenticatedUser };
      }

      return {
        success: false,
        error: data.detail || data.error || "Login failed",
      };
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const verifyTwoFactorLogin = async (challengeToken, code) => {
    const response = await api.post("/auth/2fa/verify-login", {
      challenge_token: challengeToken,
      code,
    });
    const data = response.data;
    setToken(data.access_token);
    localStorage.setItem("token", data.access_token);
    let authenticatedUser = data.user;
    try {
      const meResponse = await api.get(`/auth/me?t=${Date.now()}`);
      authenticatedUser = meResponse.data;
    } catch (error) {
      console.warn("Não foi possível atualizar o contexto após o 2FA:", error);
    }
    setUser(authenticatedUser);
    localStorage.setItem("user", JSON.stringify(authenticatedUser));
    await Promise.all([reloadMenuPreferences(), reloadClinicContexts()]);
    return { success: true, user: authenticatedUser };
  };

  const switchClinic = async (clinicId) => {
    if (!clinicId || clinicId === selectedClinicId) return true;
    await api.post('/clinic-contexts/select', { clinic_id: clinicId });
    clearClinicLocalState();
    clearOnboardingTipSession();
    const response = await api.get(`/auth/me?t=${Date.now()}`);
    const userData = response.data;
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    await Promise.all([reloadMenuPreferences(), reloadClinicContexts()]);
    window.location.assign(accessHomePath(userData));
    return true;
  };

  const createOwnClinic = async (clinicName) => {
    const response = await api.post('/clinic-contexts/own-clinic', {
      clinic_name: clinicName,
    });
    clearClinicLocalState();
    clearOnboardingTipSession();
    const meResponse = await api.get(`/auth/me?t=${Date.now()}`);
    const userData = meResponse.data;
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    await Promise.all([reloadMenuPreferences(), reloadClinicContexts()]);
    return response.data;
  };

  const register = async (email, password, fullName, clinicName, phone, assessmentId = null) => {
    try {
      const response = await api.post("/auth/register", {
        email,
        password,
        full_name: fullName,
        clinic_name: clinicName,
        phone,
        assessment_id: assessmentId || null,
      });

      const data = response.data;

      if (response.status === 200 || response.status === 201) {
        return {
          success: true,
          message:
            data.message ||
            "Conta criada com sucesso. Verifique seu e-mail para ativar a conta.",
          requiresActivation: !!data.requires_activation,
          assessmentAttached: !!data.assessment_attached,
        };
      }

      return {
        success: false,
        error: data.detail || data.error || "Falha ao criar conta",
      };
    } catch (error) {
      console.error("Register error:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (!user || !token) return;

    let stopped = false;

    const sendHeartbeat = async () => {
      if (stopped) return;

      try {
        await api.post("/admin/heartbeat", {
          current_route: window.location.pathname || "",
          user_agent: window.navigator?.userAgent || "",
        });
      } catch (error) {
        console.warn("Erro ao enviar heartbeat:", error);
      }
    };

    sendHeartbeat();

    const intervalId = window.setInterval(sendHeartbeat, 60000);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [user, token]);

  const logout = (revokeSession = true) => {
    const currentToken = localStorage.getItem("token");
    if (revokeSession && currentToken) {
      api.post("/auth/logout", null, {
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    clearOnboardingTipSession();
    setToken(null);
    setUser(null);
    setMenuPreferences({ hidden_items: [], preset: null, manually_customized: false });
    setDashboardPreferences({
      preset: null, mode: null, sections: {}, blocks: {}, manually_customized: false,
    });
    setExperiencePreferences({
      profile: null, mode: null, source: null, version: 1, changed_at: null,
    });
    setMenuPreferencesLoading(true);
    setClinicContexts([]);
    setSelectedClinicId(null);
    setCanCreateOwnClinic(false);
    setClinicContextsLoading(true);
    clearClinicLocalState();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const isAdmin = user?.role === "admin";
  const isAuthenticated = !!user && !!token;

  const value = {
    user,
    token,
    loading,
    login,
    verifyTwoFactorLogin,
    register,
    logout,
    reloadAuthStatus: verifyToken,
    menuPreferences,
    menuPreferencesLoading,
    reloadMenuPreferences,
    updateMenuPreferences,
    dashboardPreferences,
    experiencePreferences,
    updateDashboardPreferences,
    clinicContexts,
    selectedClinicId,
    clinicContextsLoading,
    canCreateOwnClinic,
    reloadClinicContexts,
    switchClinic,
    createOwnClinic,
    isAdmin,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
